import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Chip,
  Collapse,
} from "@mui/material";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  deleteDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "./SnackbarHandler";
import EssayList from "./EssayList";
import CachedIcon from "@mui/icons-material/Cached";
import { getStyles } from "../index";
import StatusFilters from "./StatusFilters";
import { formatDate } from "./functions";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { People, Description } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AssessmentStats from "./AssessmentStats";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import ClassDropdown from "./ClassDropdown";

const ClassDetail = ({ user }) => {
  const { id } = useParams();
  const theme = useTheme();
  const styles = getStyles(theme);
  const { showSnackbar } = useSnackbar();

  const [classData, setClassData] = useState(null);
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user, id]);

  const fetchData = async () => {
    if (!user || !id) return;

    setLoading(true);
    try {
      // Hämta klassdata
      const classDoc = await getDoc(doc(db, "classes", id));
      if (!classDoc.exists()) {
        showSnackbar("Klassen kunde inte hittas.", "error");
        return;
      }
      setClassData({ id: classDoc.id, ...classDoc.data() });

      // Hämta klassens uppsatser
      const essaysQuery = query(
        collection(db, "essays"),
        where("classId", "==", id),
        orderBy("createdAt", "desc"),
      );
      const essaysSnapshot = await getDocs(essaysQuery);
      const essaysList = essaysSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        className: classDoc.data().name,
      }));

      setEssays(essaysList);
    } catch (error) {
      console.error("Error fetching class data:", error);
      showSnackbar("Ett fel uppstod när data skulle hämtas.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEssaysDelete = async (essaysToDelete) => {
    try {
      await Promise.all(
        essaysToDelete.map((essay) => deleteDoc(doc(db, "essays", essay.id))),
      );

      setEssays((prevEssays) =>
        prevEssays.filter(
          (essay) =>
            !essaysToDelete.some((toDelete) => toDelete.id === essay.id),
        ),
      );

      showSnackbar(
        `${essaysToDelete.length} ${essaysToDelete.length === 1 ? "text" : "texter"} raderade.`,
        "success",
      );
    } catch (error) {
      console.error("Error deleting essays:", error);
      showSnackbar("Ett fel uppstod vid radering av texter.", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!classData) {
    return (
      <Box sx={styles.container}>
        <Typography>Klassen kunde inte hittas.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerContainer}>
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            startIcon={<GenerateIcon />}
            component={Link}
            to={`/app/uppsats/ny`}
          >
            Betygsätt ny text
          </Button>
        </Box>

        <Typography
          variant="h1"
          sx={{ ...styles.header, borderBottom: "0px", mb: 2 }}
        >
          Bedömningar för {classData.name}
        </Typography>

        <Box sx={{ display: "flex", gap: 0, mb: 2, mt: 0 }}>
          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <People sx={{ fontSize: 14 }} />
                {classData.studentCount} elever
              </Box>
            }
            size="small"
            sx={{
              ...styles.grayChip,
              height: "24px",

              ml: 0,
            }}
          />

          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Description sx={{ fontSize: 14 }} />
                {essays.length} bedömningar
              </Box>
            }
            size="small"
            sx={{
              ...styles.grayChip,
              height: "24px",
              ml: 1,
            }}
          />

          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                Skapades för {formatDate(classData.createdAt)}
              </Box>
            }
            size="small"
            sx={{
              ...styles.grayChip,
              height: "24px",
              ml: 1,
            }}
          />
        </Box>
        <Box sx={{ mb: 4, mt: 0 }}>
          <Typography
            variant="subtitle"
            sx={{ mb: 2, color: "text.secondary", display: "none" }}
          >
            {classData.description}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            background: "linear-gradient(to bottom right, #ec4899, #f97316)",
            borderRadius: 2,
            p: 3,
            mb: 4,
            cursor: "pointer",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
          onClick={() => setShowStats(!showStats)}
        >
          <ExpandMoreIcon
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              position: "absolute",
              right: 24,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 35,
              transition: "all 0.3s ease-in-out",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: 4,
              zIndex: 2,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <MilitaryTechIcon sx={{ color: "white", fontSize: 45 }} />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Visa resultattavlan
                </Typography>
                <Typography sx={{ color: "white", opacity: 0.9 }}>
                  {showStats
                    ? "Klicka för att dölja klassens resultat"
                    : "Klicka för att se klassens resultat"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Collapse
          sx={{
            "& .MuiCollapse-wrapper": {
              transition: "all 0.3s ease-in-out !important",
              transform: showStats ? "translateY(0)" : "translateY(-20px)",
              opacity: showStats ? 1 : 0,
            },
          }}
          in={showStats}
          timeout="auto"
          unmountOnExit
        >
          {!loading && essays.length > 0 && (
            <Box>
              <AssessmentStats assessments={essays} styles={styles} />
            </Box>
          )}
        </Collapse>
        {/* Filter status */}
        <StatusFilters
          essays={essays}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          theme={theme}
        />

        <Paper
          sx={{
            p: 3,
            mb: 4,

            width: { xs: "100%", md: "40%" },
            border: "1px solid #eee",

            borderRadius: 3,
            boxShadow: "0 3px 28px rgba(12,8,51,.06)",
            marginLeft: "auto",
            display: "none",
          }}
        >
          <ClassDropdown user={user} />
        </Paper>
        {/* Lista med uppsatser */}
        {essays.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Denna klass har inga bedömningar än.
          </Typography>
        ) : (
          <EssayList
            essays={essays}
            statusFilter={statusFilter}
            onEssaysDelete={handleEssaysDelete}
            onEssayUpdate={() => fetchData()}
            theme={theme}
            styles={styles}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClassDetail;
